<template>
	<v-row class="full-height process-types-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="process-types-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Search process types..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="process-types-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- Delete Button -->
						<v-col cols="auto" v-if="deleteTypes.length > 0">
							<v-btn
								depressed
								rounded
								color="error"
								@click="deleteWarning = true"
								:disabled="!canManageProcessTypes()"
							>
								Delete Selected ({{ deleteTypes.length }})
							</v-btn>
						</v-col>

						<!-- New Process Type Button -->
						<v-col cols="auto" v-if="canManageProcessTypes()">
							<v-btn
								depressed
								rounded
								color="success"
								@click="newProcessType()"
								:disabled="activeEdit !== null"
							>
								New Process Type
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 process-types-table-container">
			<v-data-table
				:headers="headers"
				:items="processTypes"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="process-types-data-table"
				must-sort
				fixed-header
				item-key="processTypeId"
			>
				<!-- Select Column -->
				<template v-slot:item.select="{ item }">
					<v-icon
						v-if="activeEdit"
						style="font-size: 2rem; cursor: move"
						@mousedown="triggerSorting($event, item)"
					>
						mdi-arrow-split-horizontal
					</v-icon>
					<v-checkbox
						v-else
						class="form-checkbox-center"
						v-model="item.selected"
						color="accent"
						hide-details="auto"
						@change="selectProcessType(item)"
						:disabled="!canManageProcessTypes()"
					></v-checkbox>
				</template>

				<!-- Name Column -->
				<template v-slot:item.name="{ item }">
					<div v-if="item === activeEdit" class="form-input">
						<v-text-field
							outlined
							dense
							hide-details="auto"
							v-model="item.name"
							:disabled="!canManageProcessTypes()"
						></v-text-field>
					</div>
					<span v-else>{{ item.name }}</span>
				</template>

				<!-- Description Column -->
				<template v-slot:item.description="{ item }">
					<div v-if="item === activeEdit" class="form-input">
						<v-text-field
							outlined
							dense
							hide-details="auto"
							v-model="item.description"
							:disabled="!canManageProcessTypes()"
						></v-text-field>
					</div>
					<span v-else>{{ item.description }}</span>
				</template>

				<!-- Exclude Attaching On Parent PO Column -->
				<template v-slot:item.excludeAttachingOnParentPO="{ item }">
					<v-checkbox
						class="form-checkbox-center"
						v-model="item.excludeAttachingOnParentPO"
						color="accent"
						hide-details="auto"
						:readonly="item !== activeEdit"
						:disabled="item !== activeEdit || !canManageProcessTypes()"
					></v-checkbox>
				</template>

				<!-- Require Attaching Column -->
				<template v-slot:item.requireAttaching="{ item }">
					<v-checkbox
						class="form-checkbox-center"
						v-model="item.requireAttaching"
						color="accent"
						hide-details="auto"
						:readonly="item !== activeEdit"
						:disabled="item !== activeEdit || !canManageProcessTypes()"
					></v-checkbox>
				</template>

				<!-- Include Parent Column -->
				<template v-slot:item.includeParent="{ item }">
					<v-checkbox
						class="form-checkbox-center"
						v-model="item.includeParent"
						color="accent"
						hide-details="auto"
						:readonly="item !== activeEdit"
						:disabled="item !== activeEdit || !canManageProcessTypes()"
					></v-checkbox>
				</template>

				<!-- Action Column -->
				<template v-slot:item.action="{ item }">
					<div v-if="item === activeEdit" class="d-flex justify-end">
						<v-btn
							icon
							color="primary"
							@click="
								item.processTypeId == null ? removeProcessType() : (activeEdit = null);
								fetchData();
							"
							:disabled="!canManageProcessTypes()"
						>
							<v-icon style="font-size: 2rem">mdi-close</v-icon>
						</v-btn>
						<v-btn
							icon
							color="primary"
							@click="saveProcessType()"
							:disabled="!canManageProcessTypes()"
						>
							<v-icon style="font-size: 2rem">mdi-check</v-icon>
						</v-btn>
					</div>
					<div v-else class="d-flex justify-end">
						<v-btn icon color="primary" @click="activeEdit = item" :disabled="!canManageProcessTypes()">
							<v-icon style="font-size: 2rem">mdi-pencil</v-icon>
						</v-btn>
					</div>
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-cog</v-icon>
						<p class="mt-2 grey--text">No process types found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>

		<WarningModal
			v-if="deleteWarning"
			:showModal="deleteWarning"
			:message="
				hasDependencies
					? dependencyMessage
					: 'Are you sure you want to delete the selected process types?'
			"
			:itemList="!hasDependencies ? deleteTypes : []"
			:itemListKey="'name'"
			:cancelText="'No, Cancel'"
			:confirmText="'Yes, Delete'"
			style="white-space: pre-line"
			@cancel="deleteWarning = false"
			@confirm="
				deleteProcessType();
				deleteWarning = false;
			"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import WarningModal from "@/components/WarningModal.vue";
import { hasPermission, SystemPermissions } from "@/models/User";
import { getApiUrl } from "@/helpers";

export default {
	name: "ProcessTypes",
	components: {
		WarningModal,
	},
	data() {
		return {
			loading: true,
			searchForm: {
				keywords: "",
			},
			processTypes: [],
			totalCount: 0,
			searchTimeout: null,
			deleteTypes: [],
			deleteWarning: false,
			activeEdit: null,
			startSort: false,
			dragItemElement: null,
			dragItem: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["sequence"],
				sortDesc: [false],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "",
					value: "select",
					sortable: false,
					width: "60px",
					align: "center",
				},
				{
					text: "Name",
					value: "name",
					sortable: true,
					width: "15%",
				},
				{
					text: "Description",
					value: "description",
					sortable: true,
					width: "20%",
				},
				{
					text: "Exclude Attaching Details on Parent Item's PO",
					value: "excludeAttachingOnParentPO",
					sortable: false,
					width: "20%",
					align: "center",
				},
				{
					text: "Sub-Items Require Attaching PO",
					value: "requireAttaching",
					sortable: false,
					width: "15%",
					align: "center",
				},
				{
					text: "Include Parent Item On Sub-Items PO",
					value: "includeParent",
					sortable: false,
					width: "15%",
					align: "center",
				},
				{
					text: "",
					value: "action",
					sortable: false,
					width: "120px",
					align: "end",
				},
			],
		};
	},
	apollo: {
		dependentPurchaseOrders: {
			query() {
				return gql`
					query GetDependentPurchaseOrders($processTypeId: Int!) {
						dependentPurchaseOrders(processTypeId: $processTypeId) {
							purchaseOrderId
							orderNumber
							processTypeId
						}
					}
				`;
			},
			variables() {
				if (!this.deleteTypes || this.deleteTypes.length === 0) {
					return { processTypeId: 0 };
				}
				return {
					processTypeId: this.deleteTypes[0].processTypeId,
				};
			},
			skip() {
				return !this.deleteTypes || this.deleteTypes.length === 0;
			},
		},
	},
	computed: {
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
		hasDependencies() {
			return this.dependentPurchaseOrders && this.dependentPurchaseOrders.length > 0;
		},
		dependencyMessage() {
			if (!this.hasDependencies) return "";

			const orderNumbers = this.dependentPurchaseOrders.map((po) => po.orderNumber).join(",\n");

			return `WARNING: This process type is referenced by the following purchase orders:\n${orderNumbers}.\n\nIf you delete this process type, those references will be removed from the purchase orders.\n\nAre you sure you want to proceed?`;
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/processTypesState"];
					if (savedState) {
						this.tableOptions.page = Math.floor(savedState.offset / 15) + 1;
						this.searchForm.keywords = savedState.keywords || "";
						if (savedState.sortBy) {
							this.tableOptions.sortBy = [savedState.sortBy];
						}
						if (savedState.sortDesc !== undefined) {
							this.tableOptions.sortDesc = [savedState.sortDesc];
						}

						this.$nextTick(() => {
							if (typeof savedState.scrollPosition === "number") {
								setTimeout(() => {
									window.scrollTo({
										top: savedState.scrollPosition,
										left: 0,
										behavior: "smooth",
									});
								}, 500);
							}
						});
					}
				}
			},
			immediate: true,
		},
	},
	methods: {
		canManageProcessTypes() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Inventory_ProcessTypes_Create_Update_Delete)) {
				return true;
			}
			return user.isAdmin;
		},

		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				name: "name",
				description: "description",
				sequence: "sequence",
			};
			return sortFieldMap[columnValue] || "sequence";
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "sequence";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldName = this.getSortFieldName(sortColumn);
			const order = [{ [sortFieldName]: sortDirection }];

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
			};
		},

		async fetchData() {
			this.loading = true;
			try {
				const variables = this.buildQueryVariables();
				const res = await this.$apollo.query({
					query: gql`
						query GetProcessTypes(
							$offset: Int!
							$take: Int!
							$order: [ProcessTypeSortInput!] = [{ sequence: ASC }]
						) {
							processTypes(skip: $offset, take: $take, order: $order) {
								items {
									processTypeId: id
									name
									description
									excludeAttachingOnParentPO
									requireAttaching
									includeParent
									sequence
								}
								totalCount
								pageInfo {
									hasNextPage
									hasPreviousPage
								}
							}
						}
					`,
					variables,
					fetchPolicy: "no-cache",
				});

				this.totalCount = res.data.processTypes.totalCount;
				this.processTypes = res.data.processTypes.items.map((item) => ({
					...item,
					selected: false,
				}));
				this.loading = false;
			} catch (error) {
				console.error("Error fetching process types:", error);
				this.loading = false;
			}
		},

		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},

		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},

		newProcessType() {
			const newProcess = {
				processTypeId: null,
				name: "",
				description: "",
				excludeAttachingOnParentPO: false,
				requireAttaching: false,
				includeParent: false,
				sequence: 0,
				selected: false,
			};

			this.processTypes.unshift(newProcess);

			//set the new process as the active edit
			this.activeEdit = newProcess;
		},

		selectProcessType(data) {
			//add or remove the selected processType to the delete list
			if (this.deleteTypes.some((obj) => obj === data)) {
				this.deleteTypes = this.deleteTypes.filter((obj) => obj !== data);
			} else {
				this.deleteTypes.push(data);
			}
		},

		removeProcessType() {
			this.processTypes.splice(0, 1);
			this.activeEdit = null;
		},

		async deleteProcessType() {
			try {
				for await (const p of this.deleteTypes) {
					const res = await this.$http.delete(getApiUrl(`/api/process-types/${p.processTypeId}`));
					console.log(res);
				}

				this.fetchData();
				this.deleteTypes = [];
			} catch (error) {
				console.log(error);
			}
		},

		async saveProcessType() {
			try {
				let res;
				if (this.activeEdit.processTypeId) {
					res = await this.$http.put(getApiUrl("/api/process-types"), this.activeEdit);
				} else {
					res = await this.$http.post(getApiUrl("/api/process-types"), this.activeEdit);
				}
				console.log(res);

				//update sequence list for newest items
				await this.updateSequenceList(res.data.processTypeId);

				//refresh latest process types and clear activeEdit
				this.fetchData();
				this.activeEdit = null;
			} catch (error) {
				console.log(error);
			}
		},

		async updateSequenceList(id) {
			try {
				//create list of sequences and ids
				const { page, itemsPerPage } = this.tableOptions;
				const pageOffset = (page - 1) * itemsPerPage;

				const sequenceList = this.processTypes.map((obj) => {
					const sequenceObj = {
						//if no id is defined then use the passed in id (that means the user created a new entry and this needs to be added to the sequence list)
						entityId: obj.processTypeId ? obj.processTypeId : id,
						//if user is using paging then we need to add the index offset to the sequence count
						sequence: pageOffset !== 0 ? obj.sequence + pageOffset : obj.sequence,
					};
					return sequenceObj;
				});

				const res = await this.$http.put(getApiUrl("/api/process-types/sequence"), {
					sequence: sequenceList,
				});
				console.log(res);

				return;
			} catch (error) {
				console.log(error);
			}
		},

		//drag and drop sorting functions
		onDrop() {
			//if sorting is active reset dragging states after dropping the dragged item
			if (this.dragItem) {
				this.startSort = false;
				this.dragItemElement.classList.remove("dragging");
				this.dragItemElement = null;
				this.dragItem = null;
			}
		},

		triggerSorting(event, item) {
			this.startSort = true;
			//set dragging element in data and add dragging class (this is removed on drag drop event)
			this.dragItemElement = event.target.closest("tr");
			this.dragItemElement.classList.add("dragging");

			//set drag item in data
			this.dragItem = item;
		},

		setItemOrder(closestItem) {
			let sortedList = [];

			//get all items except the current drag item and sort them in order
			sortedList = this.processTypes
				.filter((obj) => obj !== this.dragItem)
				.sort((a, b) => a.sequence - b.sequence);

			if (closestItem) {
				//find the closestItem in data by DOM style order property
				const closestSortItemIndex = sortedList.findIndex(
					(obj) => obj.sequence === closestItem.style.order
				);

				//assign dragItem above the passed in closestItem
				sortedList.splice(closestSortItemIndex, 0, this.dragItem);
			} else {
				//assign the selected item to the end of its level
				sortedList.push(this.dragItem);
			}

			//set the new sequence order via index + 1
			sortedList = sortedList.map((obj, i) => {
				return { ...obj, sequence: i + 1 };
			});

			//update the list with new sequences from the sortedList
			sortedList.forEach((item) => {
				const foundIndex = this.processTypes.findIndex(
					(obj) => obj.processTypeId === item.processTypeId
				);
				this.processTypes[foundIndex].sequence = item.sequence;
			});
		},

		sortItems(event) {
			//get all item elements that are not the dragged item
			const nonActiveItemElements = [
				...event.target.closest("tbody").querySelectorAll("tr:not(.dragging)"),
			];

			//find the closest item that were above in the DOM
			const closestItem = nonActiveItemElements.reduce(
				(closest, child) => {
					const box = child.getBoundingClientRect();
					const offset = event.clientY - box.top - box.height / 2;
					if (offset < 0 && offset > closest.offset) {
						return { offset: offset, element: child };
					} else {
						return closest;
					}
				},
				{ offset: Number.NEGATIVE_INFINITY }
			).element;

			//change the item order in data by element names
			if (closestItem == null) {
				this.setItemOrder();
			} else {
				this.setItemOrder(closestItem);
			}
		},
	},
	mounted() {
		// Initial fetch will happen via tableOptions watcher
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
	beforeRouteLeave(to, from, next) {
		this.$store.dispatch("globalState/saveProcessTypesState", {
			offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
			keywords: this.searchForm.keywords,
			sortBy: this.tableOptions.sortBy[0],
			sortDesc: this.tableOptions.sortDesc[0],
			scrollPosition: window.scrollY,
		});
		next();
	},
};
</script>

<style lang="scss" scoped>
.process-types-page {
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.process-types-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

.process-types-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0;
}

.process-types-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.process-types-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;

	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;
		table {
			min-width: 600px;
		}
	}

	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}

.form-input {
	width: 100%;
	margin: 0;
}

.form-checkbox-center {
	display: flex;
	justify-content: center;
	margin: 0;
	padding: 0;

	::v-deep .v-input__control {
		flex-grow: 0;
	}
}

tr.dragging {
	opacity: 0.5;
}
</style>
